/**
 * 用量页。
 *
 * 顶部全局概览（useUsageOverview）+ 按模型分布柱状图 +
 * 支持按模型/时间筛选的用量记录分页表格。
 *
 * @module pages/UsagePage
 */
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import PageHeader from '@/components/common/PageHeader';
import StatCard from '@/components/common/StatCard';
import DataTable, { type Column } from '@/components/common/DataTable';
import MoneyText from '@/components/common/MoneyText';
import UsageBarChart, { type UsageBarDatum } from '@/components/charts/UsageBarChart';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import { useUsageOverview } from '@/hooks/useUsageOverview';
import { getUsage } from '@/services/usage';
import type { UsageRecord } from '@/types';
import { formatNumber } from '@/utils/format';
import dayjs from 'dayjs';

/**
 * 用量页组件。
 *
 * @returns 页面元素。
 */
export default function UsagePage(): JSX.Element {
  const { data: overview } = useUsageOverview();
  const { Toast } = useToast();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [model, setModel] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, loading } = useApi(
    () =>
      getUsage({
        page: page + 1,
        pageSize,
        model: model || undefined,
        from: from || undefined,
        to: to || undefined,
      }),
    [page, pageSize, model, from, to],
  );

  const rows: UsageRecord[] = data?.list ?? [];
  const total = data?.total ?? 0;

  // 按模型聚合（基于当前页数据）用于柱状图。
  const byModel: UsageBarDatum[] = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r) => {
      map.set(r.model, (map.get(r.model) ?? 0) + r.totalTokens);
    });
    return Array.from(map.entries())
      .map(([model, tokens]) => ({ model, tokens }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [rows]);

  const resetFilters = (): void => {
    setModel('');
    setFrom('');
    setTo('');
    setPage(0);
  };

  const columns: Column<UsageRecord>[] = [
    {
      key: 'createdAt',
      header: '时间',
      render: (r) => dayjs(r.createdAt).format('YYYY-MM-DD HH:mm'),
    },
    { key: 'model', header: '模型' },
    {
      key: 'totalTokens',
      header: 'Tokens',
      align: 'right',
      render: (r) => formatNumber(r.totalTokens),
    },
    {
      key: 'cost',
      header: '消耗',
      align: 'right',
      render: (r) => <MoneyText value={r.cost} />,
    },
    { key: 'status', header: '状态' },
    {
      key: 'duration',
      header: '耗时',
      align: 'right',
      render: (r) => `${r.durationMs}ms`,
    },
  ];

  return (
    <Box>
      <PageHeader title="用量" subtitle="查看你的模型调用记录与消耗分布" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' },
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard
          title="请求总数"
          value={formatNumber(overview?.requestCount ?? 0)}
        />
        <StatCard
          title="Token 总量"
          value={formatNumber(overview?.totalTokens ?? 0)}
          color="#0ea5e9"
        />
        <StatCard
          title="总消耗"
          value={<MoneyText value={overview?.totalCost ?? '0'} />}
          color="#f59e0b"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <UsageBarChart data={byModel} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          label="模型"
          size="small"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="如 gpt-4o"
        />
        <TextField
          label="开始日期"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="结束日期"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => setPage(0)}
        >
          查询
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={resetFilters}
        >
          重置
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          共 {formatNumber(total)} 条记录
        </Typography>
      </Box>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={loading}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(0);
        }}
      />
      {Toast}
    </Box>
  );
}
